import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7a96b380"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "grammar-word-detail" }
const _hoisted_2 = { class: "detail-wrapper" }
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "mobile-back" }
const _hoisted_5 = ["src"]
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "back-text" }, "Back", -1))
const _hoisted_7 = { class: "page-title-wrapper" }
const _hoisted_8 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Loading knowledge component data...", -1))
const _hoisted_10 = {
  key: 1,
  class: "detail-content"
}
const _hoisted_11 = { class: "summary-card" }
const _hoisted_12 = { class: "summary-head" }
const _hoisted_13 = { class: "summary-title" }
const _hoisted_14 = { class: "level-info" }
const _hoisted_15 = { class: "level-text" }
const _hoisted_16 = {
  key: 0,
  class: "level-separator"
}
const _hoisted_17 = {
  key: 1,
  class: "level-status"
}
const _hoisted_18 = {
  key: 0,
  class: "examples"
}
const _hoisted_19 = ["innerHTML"]
const _hoisted_20 = ["innerHTML"]
const _hoisted_21 = { class: "history-table" }
const _hoisted_22 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", null, "Date"),
    /*#__PURE__*/_createElementVNode("th", null, "Question(s)"),
    /*#__PURE__*/_createElementVNode("th", null, "Status")
  ])
], -1))
const _hoisted_23 = { class: "question-cell" }
const _hoisted_24 = ["onClick"]
const _hoisted_25 = { class: "status-cell" }
const _hoisted_26 = { key: 0 }
const _hoisted_27 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("td", {
  colspan: "3",
  class: "empty-row"
}, "No activity recorded yet.", -1))
const _hoisted_28 = [
  _hoisted_27
]
const _hoisted_29 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_30 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", null, "Knowledge component not found", -1))
const _hoisted_31 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "We could not locate that grammar item for this student. Please return to the list and choose another item.", -1))
const _hoisted_32 = [
  _hoisted_30,
  _hoisted_31
]

import { computed, ref, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useLingoStore } from "@/stores/lingoStore";
import { useLearnerStore } from "@/stores/learnerStore";
import type { KnowledgeComponent, ExposureHistory } from "@/interfaces/lingo.interfaces";
import LingoTooltipIcon from "@/modules/main/components/manage/LingoTooltipIconComponent.vue";
import GrammarAnswerReviewModal from "@/modules/main/components/manage/GrammarAnswerReviewModal.vue";
import { orderPicklistAnswerLabelsByDisplay } from "@/modules/main/services/grammarAnswerReview";
import { renderInlineMarkdown, buildClozeAnswerView } from "@/modules/main/services/grammarRenderer";
import getKcStatusTooltip from "@/modules/main/utils/kc";
import { findGrammarCurriculumKc } from "@/modules/main/utils/grammarCurriculumKc";
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?component';
import SpinnerLoader from "@/components/global/SpinnerLoader.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoStudentGrammarKC',
  setup(__props) {

const isArrowIconString = typeof ArrowLeftIcon === "string";

const route = useRoute();
const lingoStore = useLingoStore();
const learnerStore = useLearnerStore();

const studentId = computed(() => String(route.params.studentId ?? ""));
const kcIdParam = computed(() => String(route.params.kcId ?? ""));
const normalizedKcId = computed(() => {
  const raw = kcIdParam.value;
  if (!raw) return null;
  return decodeURIComponent(String(raw));
});

const studentLingoData = computed(() => lingoStore.getLingoDataByStudentId(studentId.value) || null);

//breadcrumbs
const pages = computed(() => {
  const base = [{ name: "Lingo Data", path: "/main/lingo-data" }];
  base.push({
    name: "Data for " + displayName.value,
    path: "/main/lingo-data/student/" + studentId.value
  });
  base.push({
    name: `Grammar List`,
    path: "/main/lingo-grammar-list/student/" + studentId.value
  });
  base.push({
    name: kc.value?.name || "Knowledge Component",
    path: "#"
  });
  return base;
});

const grammarKCs = computed<KnowledgeComponent[]>(() => {
  const all = studentLingoData.value?.allKCs;
  return Array.isArray(all) ? all.filter(k => k && k.type === "grammar") : [];
});

const curriculumKc = computed<KnowledgeComponent | null>(() => {
  if (normalizedKcId.value == null) return null;
  return findGrammarCurriculumKc(studentLingoData.value?.grammarMatrix, normalizedKcId.value) as KnowledgeComponent | null;
});

// Resolve the KC from the student's progress first, then fall back to the
// published curriculum matrix for a KC they have not encountered yet.
const kc = computed<KnowledgeComponent | null>(() => {
  if (normalizedKcId.value == null) return null;
  const searchId = String(normalizedKcId.value);
  const all = grammarKCs.value;

  let found = all.find((k: any) => k?.strapiWordId != null && String(k.strapiWordId) === searchId);
  if (!found) {
    found = all.find((k: any) => String(k.id ?? "") === searchId || String(k._id ?? "") === searchId);
  }
  if (!found) {
    found = all.find((k: any) => typeof k?.name === "string" && k.name === searchId);
  }
  return found || curriculumKc.value || null;
});

const STATUS_LABELS: Record<string, string> = {
  not_yet_seen: "Not yet seen",
  seen: "Seen",
  learning: "Learning",
  practicing: "Reinforcing",
  reinforcing: "Reinforcing",
  mastered_prior_learning: "Mastered (Prior Learning)",
  mastered_new_learning: "Mastered (Gap Filled)",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const statusBadge = computed(() => {
  if (!kc.value) return { label: "Not available" };
  return { label: STATUS_LABELS[kc.value.status] || STATUS_LABELS.not_yet_seen };
});

const statusDescription = computed(() => getKcStatusTooltip(kc.value?.status) || "");

const hasExamples = computed(() => Boolean(kc.value && (kc.value.exampleLine1 || kc.value.exampleLine2)));

const showReviewModal = ref(false);
const activeReview = ref({
  title: "",
  timestamp: "",
  promptHtml: "",
  itemType: "",
  options: [] as Array<any>,
  blanks: [] as Array<any>,
  studentAnswer: "",
  correctAnswer: "",
  isCorrect: false
});

function renderExample(text: string): string {
  const html = renderInlineMarkdown(text || "");
  return html.replace(/^(Incorrect:|Correct:)/, "<em>$1</em>");
}

function formatTimestamp(ts: string | Date | undefined | null) {
  if (!ts) return "—";
  const d = new Date(ts as any);
  if (Number.isNaN(d.getTime())) return "—";
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${hours}:${minutes} ${ampm}`;
}

const exposureRows = computed(() => {
  if (!kc.value || !Array.isArray(kc.value.exposureHistory)) return [];
  return (kc.value.exposureHistory as ExposureHistory[])
    .slice()
    .reverse()
    .map((entry: any, idx: number) => {
      const status = entry.statusAfter || entry.status || kc.value?.status;
      const statusLabel = STATUS_LABELS[status] || status || "—";
      return {
        rowKey: `${entry.timestamp || ""}_${idx}`,
        date: formatTimestamp(entry.timestamp),
        resultLabel: entry.isCorrect ? "Correct" : "Incorrect",
        statusLabel,
        statusTooltip: getKcStatusTooltip(status) || "",
        entry
      };
    });
});

function optionText(options: any[], label: string): string {
  const option = Array.isArray(options) ? options.find(item => item && item.label === label) : null;
  return option && typeof option.text === "string" ? option.text : label || "";
}

function parseSelectedLabels(entry: any): string[] {
  const structured = entry?.itemTypeAnswer;
  if (structured && Array.isArray(structured.selectedLabels)) {
    return structured.selectedLabels.map(String);
  }

  const raw = typeof entry?.givenAnswer === "string" ? entry.givenAnswer : "";
  return raw
    .split(",")
    .map(part => part.trim())
    .filter(Boolean);
}

function parseClozeSelections(entry: any): Record<string, string> {
  const structured = entry?.itemTypeAnswer;
  if (structured?.selections && typeof structured.selections === "object") {
    return Object.keys(structured.selections).reduce((result: Record<string, string>, key) => {
      result[key] = String(structured.selections[key]);
      return result;
    }, {});
  }

  const raw = typeof entry?.givenAnswer === "string" ? entry.givenAnswer : "";
  return raw.split(",").reduce((result: Record<string, string>, part) => {
    const [blank, label] = part.split(":").map(value => value?.trim());
    if (blank && label) result[blank] = label;
    return result;
  }, {});
}

function blankSort(a: string, b: string): number {
  const aNumber = Number(String(a).replace(/^blank_/, ""));
  const bNumber = Number(String(b).replace(/^blank_/, ""));
  if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) return aNumber - bNumber;
  return String(a).localeCompare(String(b));
}

function deriveCorrectAnswer(questionData: any, itemType: string): string {
  if (!questionData) return "";
  if (itemType === "multiple_choice") {
    return optionText(questionData.options, questionData.correct_answer || "");
  }
  if (itemType === "picklist") {
    if (!Array.isArray(questionData.correct_answers)) return "";
    const labels = questionData.correct_answers.map((label: string) => String(label).trim()).filter(Boolean);
    return orderPicklistAnswerLabelsByDisplay(questionData.options, labels).join(", ");
  }
  if (itemType === "cloze_dropdown") {
    const blanks = questionData.blanks || {};
    return Object.keys(blanks)
      .sort(blankSort)
      .map(key => blanks[key]
        ? `${key}: ${optionText(blanks[key].options || [], blanks[key].correct || "")}`
        : "")
      .filter(Boolean)
      .join(", ");
  }
  return "";
}

function buildReviewOptions(questionData: any, itemType: string, entry: any): any[] {
  if (!Array.isArray(questionData.options)) return [];

  if (itemType === "multiple_choice") {
    const selected = entry.itemTypeAnswer?.selectedLabel || entry.givenAnswer || "";
    return questionData.options.map((option: any) => ({
      ...option,
      html: renderInlineMarkdown(option.text || ""),
      isSelected: option.label === selected,
      isCorrect: option.label === questionData.correct_answer,
      state: option.label === questionData.correct_answer
        ? "correct"
        : option.label === selected
          ? "incorrect"
          : "neutral"
    }));
  }

  if (itemType === "picklist") {
    const selected = new Set(parseSelectedLabels(entry));
    const correct = new Set(Array.isArray(questionData.correct_answers) ? questionData.correct_answers : []);
    return questionData.options.map((option: any) => {
      const isSelected = selected.has(option.label);
      const isCorrect = correct.has(option.label);
      return {
        ...option,
        html: renderInlineMarkdown(option.text || ""),
        isSelected,
        isCorrect,
        state: isCorrect && isSelected
          ? "correct-selected"
          : isCorrect
            ? "correct-missed"
            : isSelected
              ? "incorrect"
              : "neutral"
      };
    });
  }

  return [];
}

function buildReviewBlanks(questionData: any, entry: any): any[] {
  const selections = parseClozeSelections(entry);
  const blanks = questionData.blanks || {};
  return Object.keys(blanks)
    .sort(blankSort)
    .map(key => {
      const blank = blanks[key] || {};
      const options = Array.isArray(blank.options) ? blank.options : [];
      const selected = selections[key] || "";
      const correct = blank.correct || "";
      return {
        key,
        selected,
        selectedText: optionText(options, selected),
        correct,
        correctText: optionText(options, correct),
        isCorrect: selected === correct,
        options: options.map((option: any) => ({
          ...option,
          html: renderInlineMarkdown(option.text || ""),
          isSelected: option.label === selected,
          isCorrect: option.label === correct,
          state: option.label === correct ? "correct" : option.label === selected ? "incorrect" : "neutral"
        }))
      };
    });
}

function deriveStudentAnswer(entry: any, questionData: any, itemType: string): string {
  if (itemType === "multiple_choice") {
    const label = entry.itemTypeAnswer?.selectedLabel || entry.givenAnswer || "";
    return optionText(questionData.options, label);
  }
  if (itemType === "picklist") {
    const labels = parseSelectedLabels(entry).map(label => String(label).trim()).filter(Boolean);
    return orderPicklistAnswerLabelsByDisplay(questionData.options, labels).join(", ");
  }
  if (itemType === "cloze_dropdown") {
    const blanks = questionData.blanks || {};
    const selections = parseClozeSelections(entry);
    return Object.keys(blanks)
      .sort(blankSort)
      .map(key => `${key}: ${optionText(blanks[key]?.options || [], selections[key] || "") || "—"}`)
      .join(", ");
  }
  return entry.givenAnswer || "—";
}

function openReview(row: any) {
  const entry = row.entry || {};
  const question = entry.question || {};
  const questionData = question.questionData || {};
  const itemType = question.itemCategory || entry.itemCategory || "";

  const isCloze = itemType === "cloze_dropdown";
  activeReview.value = {
    title: kc.value?.name || "Question",
    timestamp: row.date,
    itemType,
    promptHtml: renderInlineMarkdown(typeof questionData.text === "string" ? questionData.text : ""),
    options: buildReviewOptions(questionData, itemType, entry),
    blanks: isCloze ? buildReviewBlanks(questionData, entry) : [],
    // Cloze reads as whole sentences in the modal (NLM-Test #96).
    clozeView: isCloze
      ? buildClozeAnswerView(questionData.text || "", questionData.blanks, parseClozeSelections(entry))
      : null,
    studentAnswer: deriveStudentAnswer(entry, questionData, itemType) || "—",
    correctAnswer: deriveCorrectAnswer(questionData, itemType),
    isCorrect: Boolean(entry.isCorrect)
  };
  showReviewModal.value = true;
}

const selectedStudent = computed(() => learnerStore.getLearnerById(studentId.value) || null);

function capitalise(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const displayName = computed(() => {
  const student = selectedStudent.value;
  if (!student) return 'Unknown Student';
  return `${capitalise(student.firstName)} ${capitalise(student.lastName)}`.trim();
});

const dataLoading = ref(false);

async function ensureStudentData(id: string) {
  if (!id) return;
  if (!lingoStore.getLingoDataByStudentId(id)) {
    dataLoading.value = true;
    try {
      await lingoStore.fetchStudentLingoData({ studentIds: [id] });
    } finally {
      dataLoading.value = false;
    }
  }
}

onMounted(() => ensureStudentData(studentId.value));
watch(studentId, id => ensureStudentData(id));

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_page_title = _resolveComponent("page-title")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_router_link, {
            class: "back-btn",
            to: '/main/lingo-grammar-list/student/' + (studentId.value || ''),
            "aria-label": "Back to grammar list"
          }, {
            default: _withCtx(() => [
              (!isArrowIconString)
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(ArrowLeftIcon)), {
                    key: 0,
                    class: "back-icon"
                  }))
                : (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: _unref(ArrowLeftIcon),
                    alt: "Back",
                    class: "back-icon"
                  }, null, 8, _hoisted_5)),
              _hoisted_6
            ]),
            _: 1
          }, 8, ["to"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(_component_page_title, {
            pages: pages.value,
            hideFilters: true,
            highlightLast: true
          }, null, 8, ["pages"])
        ])
      ]),
      _createVNode(GrammarAnswerReviewModal, {
        modelValue: showReviewModal.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showReviewModal).value = $event)),
        review: activeReview.value,
        onClose: _cache[1] || (_cache[1] = ($event: any) => (showReviewModal.value = false))
      }, null, 8, ["modelValue", "review"]),
      (dataLoading.value && !kc.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(SpinnerLoader),
            _hoisted_9
          ]))
        : (kc.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("article", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("h1", _hoisted_13, _toDisplayString(kc.value.name || "Grammar knowledge component"), 1),
                  _createElementVNode("div", _hoisted_14, [
                    _createElementVNode("span", _hoisted_15, " Level " + _toDisplayString(kc.value.level ?? "—"), 1),
                    (statusBadge.value.label)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_16, "|"))
                      : _createCommentVNode("", true),
                    (statusBadge.value.label)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(statusBadge.value.label), 1))
                      : _createCommentVNode("", true),
                    (statusDescription.value)
                      ? (_openBlock(), _createBlock(LingoTooltipIcon, {
                          key: 2,
                          class: "level-tooltip",
                          text: statusDescription.value,
                          placement: "top",
                          size: "md",
                          ariaLabel: statusBadge.value.label
                        }, null, 8, ["text", "ariaLabel"]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                (hasExamples.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                      (kc.value.exampleLine1)
                        ? (_openBlock(), _createElementBlock("p", {
                            key: 0,
                            class: "example",
                            innerHTML: renderExample(kc.value.exampleLine1)
                          }, null, 8, _hoisted_19))
                        : _createCommentVNode("", true),
                      (kc.value.exampleLine2)
                        ? (_openBlock(), _createElementBlock("p", {
                            key: 1,
                            class: "example",
                            innerHTML: renderExample(kc.value.exampleLine2)
                          }, null, 8, _hoisted_20))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_21, [
                  _createElementVNode("table", null, [
                    _hoisted_22,
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(exposureRows.value, (row) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: row.rowKey,
                          class: "summary-row"
                        }, [
                          _createElementVNode("td", null, _toDisplayString(row.date), 1),
                          _createElementVNode("td", _hoisted_23, [
                            _createElementVNode("button", {
                              type: "button",
                              class: "question-cell__action",
                              onClick: ($event: any) => (openReview(row))
                            }, _toDisplayString(row.resultLabel), 9, _hoisted_24)
                          ]),
                          _createElementVNode("td", null, [
                            _createElementVNode("div", _hoisted_25, [
                              _createElementVNode("span", null, _toDisplayString(row.statusLabel), 1),
                              (row.statusTooltip)
                                ? (_openBlock(), _createBlock(LingoTooltipIcon, {
                                    key: 0,
                                    text: row.statusTooltip,
                                    placement: "top",
                                    class: "status-cell__tooltip",
                                    ariaLabel: row.statusLabel
                                  }, null, 8, ["text", "ariaLabel"]))
                                : _createCommentVNode("", true)
                            ])
                          ])
                        ]))
                      }), 128)),
                      (!exposureRows.value.length)
                        ? (_openBlock(), _createElementBlock("tr", _hoisted_26, _hoisted_28))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_29, _hoisted_32))
    ])
  ]))
}
}

})